import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiConfigService } from '../../../core/api/api-config.service';
import { PlatformService } from '../../../core/services/platform.service';
import {
  TenantModule,
  TenantModuleDTO,
  SubscriptionSummary,
  SubscriptionSummaryDTO,
  ModuleCategory,
  ModuleStatus,
  BillingType,
} from '../modulos.models';

const VALID_CATEGORIES = new Set<ModuleCategory>([
  'marketplaces', 'erps', 'logistica', 'financeiro',
  'comunicacao', 'ia', 'monitoramento', 'bi', 'crm', 'cilindros', 'outros',
]);

const VALID_STATUSES = new Set<ModuleStatus>([
  'disponivel', 'contratado', 'ativo', 'pausado', 'teste', 'sob-consulta',
]);

const VALID_BILLING = new Set<BillingType>([
  'mensal', 'anual', 'por-uso', 'sob-consulta', 'gratuito',
]);

// Exportado para o teste de mapeamento (ancorar no `category` que o backend devolve).
export function toCategory(raw: string): ModuleCategory {
  return VALID_CATEGORIES.has(raw as ModuleCategory) ? (raw as ModuleCategory) : 'outros';
}

function toStatus(raw: string): ModuleStatus {
  return VALID_STATUSES.has(raw as ModuleStatus) ? (raw as ModuleStatus) : 'disponivel';
}

function toBilling(raw: string): BillingType {
  return VALID_BILLING.has(raw as BillingType) ? (raw as BillingType) : 'mensal';
}

@Injectable({ providedIn: 'root' })
export class ModulosService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(ApiConfigService);
  private readonly platform = inject(PlatformService);

  getModules(): Observable<TenantModule[]> {
    return this.http.get<TenantModuleDTO[]>(this.api.url('/api/tenant-modules')).pipe(
      map(dtos => dtos.map(dto => this.mapDto(dto))),
    );
  }

  getSubscriptionSummary(): Observable<SubscriptionSummary> {
    return this.http.get<SubscriptionSummaryDTO>(
      this.api.url('/api/tenant-modules/subscription-summary'),
    );
  }

  contractModule(id: string): Observable<void> {
    return this.http.post<void>(this.api.url(`/api/tenant-modules/${id}/contract`), null);
  }

  pauseModule(id: string): Observable<void> {
    return this.http.post<void>(this.api.url(`/api/tenant-modules/${id}/pause`), null);
  }

  reactivateModule(id: string): Observable<void> {
    return this.http.post<void>(this.api.url(`/api/tenant-modules/${id}/reactivate`), null);
  }

  /** Contrata um plano inteiro: cria/atualiza assinatura e provisiona as integrações incluídas. */
  contractPlan(planId: string): Observable<SubscriptionSummary> {
    return this.http.post<SubscriptionSummaryDTO>(
      this.api.url(`/api/tenant-modules/plan/${planId}/contract`), null,
    );
  }

  private mapDto(dto: TenantModuleDTO): TenantModule {
    return {
      id:             dto.id,
      name:           dto.name,
      category:       toCategory(dto.category),
      status:         toStatus(dto.status),
      priceMonthly:   dto.priceMonthly,
      billingType:    toBilling(dto.billingType),
      trialDaysLeft:  dto.trialDaysLeft,
      features:       dto.features ?? [],
      lastSync:       dto.lastSync,
      ordersToday:    dto.ordersToday,
      openErrors:     dto.openErrors,
      nextBillingDate: dto.nextBillingDate,
      // iconUrl derivado via PlatformService (API retorna null por design)
      iconUrl: this.platform.getIconUrl(dto.id),
    };
  }
}
